import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useIntl, useSelector } from 'umi';
import {
  hasBeenShownToday,
  markAsShownToday,
  hasLongAbsence,
  clearLeaveTime,
  getRandomReturnPrompt,
} from '@home/utils/bubbleHelpers';
import { shouldSuppressGreetingBubble } from './useForceCopilotPopup';

interface IHomeSelectorState {
  home?: {
    userInfo?: {
      openid?: string;
      username?: string;
      usename?: string;
    };
  };
}

// 用户主动关闭标记（只有点击 × 才写入，写入后本 session 不再弹出）
const SESSION_BUBBLE_DISMISSED_KEY = '__dba_agent_bubble_dismissed__';

// 气泡自动消失延迟（ms）
const BUBBLE_AUTO_DISMISS_DELAY = 10000;

interface IUseBubbleLogicReturn {
  showBubble: boolean;
  /** 离开>1h 返回时的随机提示词；为 null 时展示默认每日问候文案 */
  bubblePrompt: string | null;
  dismissBubble: () => void;
  permanentlyDismissBubble: () => void;
}

/**
 * 气泡显示逻辑 Hook
 * 处理气泡的显示、自动消失、用户交互打断等逻辑
 *
 * 触发条件（互不影响，均受"永久关闭"标记约束）：
 * 1. 每天首次访问 → 展示默认问候文案（bubblePrompt = null）
 * 2. 离开页面超过 1h 后回来 → 展示随机提示词（bubblePrompt = string）
 */
export const useBubbleLogic = (): IUseBubbleLogicReturn => {
  const [showBubble, setShowBubble] = useState(false);
  const [bubblePrompt, setBubblePrompt] = useState<string | null>(null);
  const { locale } = useIntl();

  const userInfo = useSelector(
    (state: IHomeSelectorState) => state.home?.userInfo,
    shallowEqual,
  );
  const userId
    = userInfo?.username || userInfo?.usename || userInfo?.openid || '';

  // 临时关闭：仅隐藏气泡，不写永久标记
  const dismissBubble = useCallback(() => {
    setShowBubble(false);
    setBubblePrompt(null);
  }, []);

  // 永久关闭：用户主动点击 × 时调用，本 session 内不再弹出任何引导气泡
  const permanentlyDismissBubble = useCallback(() => {
    setShowBubble(false);
    setBubblePrompt(null);
    try {
      sessionStorage.setItem(SESSION_BUBBLE_DISMISSED_KEY, '1');
    } catch (error) {
      console.warn('[useBubbleLogic] Failed to write to sessionStorage:', error);
    }
  }, []);

  // 触发条件 1：每天首次弹出一次，随机抽取一条提示词
  useEffect(() => {
    try {
      const isPermanentlyDismissed = sessionStorage.getItem(SESSION_BUBBLE_DISMISSED_KEY);
      // 当日已通过强制弹窗强引导过，则不再展示 greeting 气泡，避免重复引导
      if (
        !isPermanentlyDismissed
        && !hasBeenShownToday()
        && !shouldSuppressGreetingBubble(userId)
      ) {
        setBubblePrompt(getRandomReturnPrompt(locale));
        setShowBubble(true);
        markAsShownToday();
        const timer = setTimeout(() => dismissBubble(), BUBBLE_AUTO_DISMISS_DELAY);
        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.warn('[useBubbleLogic] Failed to access sessionStorage:', error);
    }
  }, [locale, dismissBubble, userId]);

  // 触发条件 2（mount 时）：重新打开页面，且距离上次离开超过 1h
  useEffect(() => {
    try {
      const isPermanentlyDismissed = sessionStorage.getItem(SESSION_BUBBLE_DISMISSED_KEY);
      if (
        !isPermanentlyDismissed
        && hasLongAbsence()
        && !shouldSuppressGreetingBubble(userId)
      ) {
        const prompt = getRandomReturnPrompt(locale);
        setBubblePrompt(prompt);
        setShowBubble(true);
        clearLeaveTime();
        const timer = setTimeout(() => dismissBubble(), BUBBLE_AUTO_DISMISS_DELAY);
        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.warn('[useBubbleLogic] Failed to check long absence on mount:', error);
    }
  }, [locale, dismissBubble, userId]);

  // 触发条件 2（切回 Tab 时）：Tab 从隐藏切换回可见，且距离上次离开超过 1h
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      try {
        const isPermanentlyDismissed = sessionStorage.getItem(SESSION_BUBBLE_DISMISSED_KEY);
        if (
          !isPermanentlyDismissed
          && hasLongAbsence()
          && !shouldSuppressGreetingBubble(userId)
        ) {
          const prompt = getRandomReturnPrompt(locale);
          setBubblePrompt(prompt);
          setShowBubble(true);
          clearLeaveTime();
          timer = setTimeout(() => dismissBubble(), BUBBLE_AUTO_DISMISS_DELAY);
        }
      } catch (error) {
        console.warn('[useBubbleLogic] Failed to check long absence on visibility change:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timer) clearTimeout(timer);
    };
  }, [locale, dismissBubble, userId]);

  // 气泡展示时，任何用户操作（点击/滚动）均打断气泡
  useEffect(() => {
    if (!showBubble) return;

    document.addEventListener('click', dismissBubble);
    window.addEventListener('scroll', dismissBubble, true);

    return () => {
      document.removeEventListener('click', dismissBubble);
      window.removeEventListener('scroll', dismissBubble, true);
    };
  }, [showBubble, dismissBubble]);

  return { showBubble, bubblePrompt, dismissBubble, permanentlyDismissBubble };
};
